import type { KpiResultType } from '@carrefour-gcs/shared';
import i18next, { t } from 'i18next';

/** Resolve the locale to format with: explicit value, else the app's current language, else English. */
const resolveLocale = (locale?: string): string => locale ?? i18next.resolvedLanguage ?? 'en';

/**
 * Format an indicator title from its value.
 *
 * @param data Object containing the value to format.
 * @returns The string representation of the value, or an empty string.
 */
export function formatIndicatorTitle(data: Pick<KpiResultType, 'value'>): string {
  return data.value !== null && data.value !== undefined ? String(data.value) : '';
}

/**
 * Format a percentage value with the locale's decimal separator (comma for fr/es, dot for en).
 *
 * @param value The already-computed percentage (e.g. 24.1 for 24.1%).
 * @param options.fractionDigits Number of decimals to show (defaults to 1).
 * @param options.withPlusSign Whether to show a leading '+' on positive values (and on zero).
 * @param options.locale BCP-47 locale; defaults to the app's current language, then English.
 * @example formatPercentValue(24.11, { locale: 'fr-FR' }) // => '24,1%'
 */
export function formatPercentValue(
  value: number,
  {
    fractionDigits = 1,
    withPlusSign = false,
    locale
  }: { fractionDigits?: number; withPlusSign?: boolean; locale?: string } = {}
): string {
  const formattedValue = Intl.NumberFormat(resolveLocale(locale), {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
    signDisplay: withPlusSign ? 'always' : 'auto'
  }).format(value);

  return `${formattedValue}%`;
}

/**
 * Number of decimals needed so that axis ticks spaced by `step` never collapse into duplicate labels.
 * Every tick of an axis must share the same number of decimals, so it is derived from the step
 * between ticks, not from each tick value.
 *
 * @param step The (absolute) distance between two consecutive axis ticks.
 * @param maxDigits Upper bound on the number of decimals (defaults to 4).
 * @returns A number of decimals between 1 and `maxDigits`.
 * @example getFractionDigitsFromStep(0.03) // => 2  (-0.03, -0.06, -0.09, -0.12...)
 * @example getFractionDigitsFromStep(0.25) // => 2  (0.25, 0.50, 0.75...)
 * @example getFractionDigitsFromStep(0.5) // => 1
 * @example getFractionDigitsFromStep(5) // => 1
 */
export function getFractionDigitsFromStep(step: number, maxDigits = 4): number {
  const absStep = Math.abs(step);
  if (!Number.isFinite(absStep) || absStep === 0) {
    return 1;
  }

  // Smallest number of decimals that represents the step exactly (tolerating float noise, e.g. 0.30000000000000004)
  for (let digits = 0; digits <= maxDigits; digits++) {
    const factor = 10 ** digits;
    if (Math.abs(Math.round(absStep * factor) - absStep * factor) < 1e-6) {
      return Math.max(digits, 1);
    }
  }

  // Step that cannot be represented with `maxDigits` decimals (e.g. 1/3): fall back to its order of magnitude
  const digits = Math.ceil(-Math.log10(absStep) - 1e-9);
  return Math.min(Math.max(digits, 1), maxDigits);
}

/**
 * Format an indicator value to a readable string. For numbers, the function will
 * crop to one decimal place, localize the thousands separator, append the given
 * unit, and use K or M suffixes for large numbers. If the value is a string
 * and `applyToString` is true, it will return the translated string.
 *
 * @param options The formatting options.
 * @param options.indicator The indicator object containing the value and unit.
 * @param options.withPlusSign Whether to prepend a plus sign to positive numbers.
 * @param options.language The locale to use for number formatting (defaults to the app's current language, then 'en').
 * @param options.applyToString Whether to translate string values instead of ignoring them (defaults to false).
 * @param options.isChart Whether to adapt decimal precision for charts (< 1 to 2 decimals, < 0.01 to 3 decimals) to prevent duplicate axis labels.
 * @param options.fractionDigits Force the number of decimals (ignored when a K/M suffix is used).
 * @returns The formatted indicator value.
 * @example formatIndicatorValue({ indicator: { value: 123456789, unit: '€' } }) // => '123.5M €'
 * @example formatIndicatorValue({ indicator: { value: 0.21, unit: '€' }, isChart: true }) // => '0.21 €'
 */
export function formatIndicatorValue({
  indicator,
  withPlusSign,
  language,
  applyToString = false,
  isChart = false,
  fractionDigits: forcedFractionDigits
}: {
  indicator?: KpiResultType;
  withPlusSign?: boolean;
  language?: string;
  applyToString?: boolean;
  isChart?: boolean;
  fractionDigits?: number;
}): string {
  if (!indicator) {
    return '';
  }

  if (typeof indicator.value === 'string' && applyToString) {
    return t(indicator.value);
  }

  if (typeof indicator.value !== 'number' && indicator.value !== null) {
    return '';
  }

  let prefix = '';
  if (typeof indicator.value === 'number' && indicator.value > 0 && withPlusSign) {
    prefix = '+';
  }

  // Internationalize number
  let formattedValue: string;
  let suffix: string = '';
  if (indicator.value === null) {
    formattedValue = '--';
  } else {
    let indicatorValue = indicator.value;

    // Define number suffix
    if (Math.abs(indicator.value) >= 1e6) {
      indicatorValue = indicator.value / 1e6;
      suffix = 'M';
    } else if (Math.abs(indicator.value) >= 1e3) {
      indicatorValue = indicator.value / 1e3;
      suffix = 'K';
    }

    let minDigits = suffix === '' ? 1 : 0;
    let maxDigits = 1;

    if (isChart) {
      let fractionDigits = suffix === '' ? 1 : 0;
      if (forcedFractionDigits !== undefined && suffix === '') {
        // Explicit precision (e.g. computed from an axis tick step): same decimals for every tick
        fractionDigits = forcedFractionDigits;
      } else if (suffix === '' && indicatorValue !== 0) {
        // Per-value precision: keeps very small values readable
        const absValue = Math.abs(indicatorValue);
        if (absValue < 0.01) {
          fractionDigits = 3;
        } else if (absValue < 1) {
          fractionDigits = 2;
        }
      }
      minDigits = fractionDigits;
      maxDigits = suffix === '' ? fractionDigits : 1;
    }

    formattedValue = Intl.NumberFormat(resolveLocale(language), {
      minimumFractionDigits: minDigits,
      maximumFractionDigits: maxDigits,
      compactDisplay: 'short',
      style: 'decimal'
    }).format(indicatorValue);
  }

  const indicatorUnitLabel = ['uvc', 'decimal', 'integer'].includes(indicator.unit)
    ? ''
    : (indicator.unitLabel ?? indicator.unit);

  return `${prefix}${formattedValue}${suffix ?? ''} ${indicatorUnitLabel}`.trim();
}
